import { lazy, Suspense, useMemo, useState } from "react";
import { ToolContainer } from "@/components/ToolLayout";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { parseNumber } from "./numberInput";
import { gstCalc, rupeesInWords, type GstSupply } from "./gst";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { RatesNote } from "./RatesNote";
import { ToolInfoSection } from "@/components/ToolInfoSection";
import { useLogToolUsage } from "@/hooks/useLogToolUsage";
import { amountError } from "./calcChecks";
import { Button } from "@/components/ui/button";
import { reportTable } from "@/lib/calc/report";
import type { TableColumn } from "@/lib/calc/table";
import { gstInvoice, gstInvoiceSteps, findGstRate, GST_RATE_FINDER_SOURCE, type GstLine, type GstRateGroup } from "./gstInvoice";
import { NoticeBox } from "@/components/NoticeBox";

const CalcReport = lazy(() => import("@/components/calc/CalcReport"));
interface LineInput { id: number; description: string; amount: string; rate: string }
type View = "single" | "invoice" | "rate";
type TaxRow = Partial<GstRateGroup> & { label: string };

// India's GST structure changed significantly under the "GST 2.0" reform,
// effective 22 September 2025 -- the old 12% and 28% slabs were abolished
// (using them on an invoice after that date is non-compliant). The
// current structure has 4 main slabs (0%, 5%, 18%, and a new 40% rate for
// luxury/sin goods), plus two niche rates that still exist for precious
// metals/stones specifically. Verified directly against multiple current
// sources rather than assumed, since this genuinely changed.
const GST_RATES = [0, 0.25, 3, 5, 18, 40];

const TOOL_INFO_CONTENT = {
  what: `Calculates India GST (Goods and Services Tax) on a price, either adding GST to a base price or working backward from a GST-inclusive price to find the base amount.`,
  how: `Enter your amount, choose whether to add or remove GST, pick the GST rate, and choose the type of supply: within one state (CGST + SGST), between states (IGST) or to a union territory without a legislature (CGST + UTGST). Amounts are shown in Indian format with the total in words for invoices.`,
  tip: `India's GST has 4 main slabs since the September 2025 reform: 0% (essentials), 5% and 18% (most goods and services), and 40% (luxury and "sin" goods like aerated drinks). Two niche rates also still apply specifically: 0.25% for rough diamonds and 3% for gold, silver, and other precious metals. The older 12% and 28% slabs that existed before September 2025 are gone for almost all goods; 28% remains only for tobacco and pan masala, which this calculator's rate list does not include.`,
};

const inr = (n: number) => `₹${n.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function GstCalculator() {
  const [amount, setAmount] = useState("1000");
  const [rate, setRate] = useState("18");
  const [mode, setMode] = useState<"exclusive" | "inclusive">("exclusive");
  const [supply, setSupply] = useState<GstSupply>("intra");
  const [view, setView] = useState<View>("single");
  const [lines, setLines] = useState<LineInput[]>([{ id: 1, description: "", amount: "1000", rate: "18" }, { id: 2, description: "", amount: "500", rate: "5" }]);
  const [nextId, setNextId] = useState(3);
  const [query, setQuery] = useState("");

  const amountErr = amountError(amount, "an amount");
  const res = gstCalc(Math.max(0, parseNumber(amount) ?? 0), Number(rate) || 0, mode === "inclusive", supply);
  const { blocked, gate } = useLogToolUsage(true, 3000);
  const inclusive = mode === "inclusive";
  const parsedLines = lines.map((l) => ({ ...l, n: parseNumber(l.amount) }));
  const badLine = parsedLines.find((l) => l.amount.trim() !== "" && (l.n === null || l.n < 0));
  const usable: GstLine[] = parsedLines.filter((l) => l.n !== null && l.n >= 0 && l.amount.trim() !== "").map((l) => ({ description: l.description, amount: l.n as number, ratePct: Number(l.rate) || 0 }));
  const inv = useMemo(() => (view === "invoice" && usable.length ? gstInvoice(usable, inclusive, supply) : null), [view, lines, inclusive, supply]); // eslint-disable-line react-hooks/exhaustive-deps
  // The single-amount view is a one-line invoice for the working and export: a test holds it equal to gstCalc.
  const single = useMemo(() => (view === "single" && !amountErr ? gstInvoice([{ amount: Math.max(0, parseNumber(amount) ?? 0), ratePct: Number(rate) || 0 }], inclusive, supply) : null), [view, amount, rate, inclusive, supply, amountErr]);
  const rep = inv ?? single;
  const rows: TaxRow[] = rep ? [...rep.groups.map((g) => ({ ...g, label: `${g.ratePct}%` })), ...(rep.groups.length > 1 ? [{ label: "Total", lines: usable.length, base: rep.base, gst: rep.gst, cgst: rep.cgst, sgst: rep.sgst, utgst: rep.utgst, igst: rep.igst, total: rep.total }] : [])] : [];
  const second = supply === "ut" ? "UTGST" : "SGST";
  const taxColumns: TableColumn<TaxRow>[] = [
    { key: "rate", header: "Rate", get: (r) => r.label },
    ...(view === "invoice" ? [{ key: "lines", header: "Lines", get: (r: TaxRow) => r.lines ?? "", align: "right" as const }] : []),
    { key: "base", header: "Taxable value", get: (r) => r.base ?? 0, format: (r) => inr(r.base ?? 0), align: "right" },
    ...(supply === "inter" ? [{ key: "igst", header: "IGST", get: (r: TaxRow) => r.igst ?? 0, format: (r: TaxRow) => inr(r.igst ?? 0), align: "right" as const }]
      : [{ key: "cgst", header: "CGST", get: (r: TaxRow) => r.cgst ?? 0, format: (r: TaxRow) => inr(r.cgst ?? 0), align: "right" as const }, { key: "second", header: second, get: (r: TaxRow) => (supply === "ut" ? r.utgst : r.sgst) ?? 0, format: (r: TaxRow) => inr((supply === "ut" ? r.utgst : r.sgst) ?? 0), align: "right" as const }]),
    { key: "gst", header: "Total GST", get: (r) => r.gst ?? 0, format: (r) => inr(r.gst ?? 0), align: "right" },
    { key: "total", header: "Total", get: (r) => r.total ?? 0, format: (r) => inr(r.total ?? 0), align: "right" },
  ];
  const found = useMemo(() => findGstRate(query), [query]);
  const setLine = (id: number, patch: Partial<LineInput>) => setLines((ls) => ls.map((l) => (l.id === id ? { ...l, ...patch } : l)));
  const addLine = (description = "", rateStr = "18") => { setLines((ls) => (ls.length >= 50 ? ls : [...ls, { id: nextId, description, amount: "", rate: rateStr }])); setNextId((n) => n + 1); };

  const cell = (label: string, v: number, id: string) => (
    <div className="text-center p-3 rounded-lg border bg-card"><p className="text-xs text-muted-foreground">{label}</p><p className="text-sm font-bold" data-testid={id}>{inr(v)}</p></div>
  );

  return (
    <ToolContainer>
      <div className={`${view === "single" ? "max-w-md" : "max-w-3xl"} mx-auto space-y-6`}>
        <Tabs value={view} onValueChange={(v) => setView(v as View)}>
          <TabsList className="w-full">
            <TabsTrigger value="single" className="flex-1 px-1 text-xs sm:text-sm">Single amount</TabsTrigger>
            <TabsTrigger value="invoice" className="flex-1 px-1 text-xs sm:text-sm">Invoice lines</TabsTrigger>
            <TabsTrigger value="rate" className="flex-1 px-1 text-xs sm:text-sm">Find a rate</TabsTrigger>
          </TabsList>
        </Tabs>

        {view !== "rate" && <Tabs value={mode} onValueChange={(v) => setMode(v as "exclusive" | "inclusive")}>
          <TabsList className="w-full">
            <TabsTrigger value="exclusive" className="flex-1">Add GST</TabsTrigger>
            <TabsTrigger value="inclusive" className="flex-1">Remove GST</TabsTrigger>
          </TabsList>
        </Tabs>}

        {view === "single" && <div>
          <Label>{mode === "exclusive" ? "Amount (excl. GST)" : "Amount (incl. GST)"} (₹)</Label>
          <Input inputMode="decimal" aria-label="Amount" aria-invalid={!!amountErr} value={amount} onChange={(e) => setAmount(e.target.value)} />
          {amountErr && <p role="alert" className="text-xs text-destructive mt-1">{amountErr}</p>}
        </div>}

        {view !== "rate" && <div className="grid grid-cols-2 gap-3">
          {view === "single" ? <div>
            <Label className="text-xs">GST Rate</Label>
            <select className="border rounded px-2 py-2 w-full bg-background text-sm" aria-label="GST rate" value={rate} onChange={(e) => setRate(e.target.value)}>
              {GST_RATES.map((pct) => <option key={pct} value={String(pct)}>{pct}%</option>)}
            </select>
          </div> : <div />}
          <div>
            <Label className="text-xs">Type of supply</Label>
            <select className="border rounded px-2 py-2 w-full bg-background text-sm" aria-label="Type of supply" value={supply} onChange={(e) => setSupply(e.target.value as GstSupply)}>
              <option value="intra">Within a state (CGST + SGST)</option>
              <option value="inter">Between states (IGST)</option>
              <option value="ut">Union territory (CGST + UTGST)</option>
            </select>
          </div>
        </div>}

        {view !== "single" ? null : amountErr ? null : blocked ? (
          <p className="text-sm text-center text-muted-foreground py-4">— blocked at today's usage limit —</p>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-3 pt-4 border-t">
              {cell("Base Amount", res.base, "gst-base")}
              {cell("Total GST", res.gst, "gst-total-gst")}
              {supply === "inter" ? cell(`IGST (${rate}%)`, res.igst, "gst-igst") : (
                <>
                  {cell(`CGST (${(Number(rate) || 0) / 2}%)`, res.cgst, "gst-cgst")}
                  {supply === "intra" ? cell(`SGST (${(Number(rate) || 0) / 2}%)`, res.sgst, "gst-sgst") : cell(`UTGST (${(Number(rate) || 0) / 2}%)`, res.utgst, "gst-utgst")}
                </>
              )}
            </div>
            <div className="p-4 rounded-xl border bg-primary/5 text-center">
              <p className="text-xs text-muted-foreground">Total Amount</p>
              <p className="text-2xl font-bold text-primary" data-testid="gst-total">{inr(res.total)}</p>
              <p className="text-xs text-muted-foreground mt-1" data-testid="gst-words">{rupeesInWords(res.total)}</p>
            </div>
          </>
        )}
        {view === "invoice" && (
          <div className="space-y-4" data-testid="gst-invoice">
            <p className="text-sm text-muted-foreground">{inclusive ? "Each amount already includes GST." : "Each amount is before GST."} Lines at the same rate are added, then GST is worked out once per rate.</p>
            <div className="space-y-2">
              {lines.map((l, i) => (
                <div key={l.id} className="grid grid-cols-12 gap-2 items-end">
                  <div className="col-span-12 sm:col-span-5"><Label className="text-xs">Item {i + 1} (optional)</Label><Input aria-label={`Description ${i + 1}`} value={l.description} onChange={(e) => setLine(l.id, { description: e.target.value })} /></div>
                  <div className="col-span-5 sm:col-span-3"><Label className="text-xs">Amount (₹)</Label><Input inputMode="decimal" aria-label={`Amount ${i + 1}`} value={l.amount} onChange={(e) => setLine(l.id, { amount: e.target.value })} /></div>
                  <div className="col-span-4 sm:col-span-2"><Label className="text-xs">Rate</Label>
                    <select className="border rounded px-2 py-2 w-full bg-background text-sm" aria-label={`GST rate ${i + 1}`} value={l.rate} onChange={(e) => setLine(l.id, { rate: e.target.value })}>{GST_RATES.map((pct) => <option key={pct} value={String(pct)}>{pct}%</option>)}</select></div>
                  <div className="col-span-3 sm:col-span-2"><Button type="button" variant="outline" size="sm" className="w-full" disabled={lines.length <= 1} onClick={() => setLines((ls) => ls.filter((x) => x.id !== l.id))} aria-label={`Remove line ${i + 1}`}>Remove</Button></div>
                </div>
              ))}
            </div>
            <Button type="button" variant="outline" size="sm" onClick={() => addLine()} disabled={lines.length >= 50}>Add a line</Button>
            {badLine && <p role="alert" className="text-xs text-destructive">Amounts must be numbers of 0 or more, like 1,250.50.</p>}
            {!blocked && inv && (
              <div className="space-y-3 pt-4 border-t">
                <div className="p-4 rounded-xl border bg-primary/5 text-center">
                  <p className="text-xs text-muted-foreground">Invoice total</p>
                  <p className="text-2xl font-bold text-primary" data-testid="gst-inv-total">{inr(inv.total)}</p>
                  <p className="text-xs text-muted-foreground mt-1" data-testid="gst-inv-words">{inv.words}</p>
                  <p className="text-xs text-muted-foreground mt-1">Taxable value {inr(inv.base)} · {supply === "inter" ? `IGST ${inr(inv.igst)}` : `CGST ${inr(inv.cgst)} + ${second} ${inr(supply === "ut" ? inv.utgst : inv.sgst)}`}</p>
                </div>
              </div>
            )}
          </div>
        )}

        {view === "rate" && (
          <div className="space-y-4" data-testid="gst-finder">
            <div>
              <Label>Item name, or the start of an HSN code</Label>
              <Input aria-label="Find a GST rate" placeholder="for example: paneer, television, 8712" value={query} onChange={(e) => setQuery(e.target.value)} />
            </div>
            {query.trim() && found.length === 0 && <NoticeBox className="text-sm">Not in this short list. It does not mean the item has no GST: look the item or its HSN/SAC code up on the CBIC rate notifications (cbic-gst.gov.in).</NoticeBox>}
            <ul className="space-y-2">
              {found.map((e) => (
                <li key={e.name} className="border rounded-lg p-3 flex flex-wrap items-center gap-3" data-testid="gst-find-row">
                  <div className="flex-1 min-w-[200px]">
                    <p className="text-sm font-medium">{e.name}</p>
                    {e.hsn && <p className="text-xs text-muted-foreground">HSN {e.hsn}</p>}
                    {e.note && <p className="text-xs text-muted-foreground">{e.note}</p>}
                  </div>
                  {e.rate === null ? <span className="text-sm font-semibold">Check CBIC</span> : <span className="text-lg font-bold text-primary" data-testid="gst-find-rate">{e.rate}%</span>}
                  {e.rate !== null && <div className="flex gap-2">
                    <Button type="button" size="sm" variant="outline" onClick={() => { setRate(String(e.rate)); setView("single"); }}>Use for one amount</Button>
                    <Button type="button" size="sm" variant="outline" onClick={() => { addLine(e.name, String(e.rate)); setView("invoice"); }}>Add as an invoice line</Button>
                  </div>}
                </li>
              ))}
            </ul>
            <NoticeBox className="text-xs" testId="gst-find-source">Suggested rates are a guide only: confirm with CBIC before invoicing. {GST_RATE_FINDER_SOURCE.text} Checked {GST_RATE_FINDER_SOURCE.readOn}. {GST_RATE_FINDER_SOURCE.caveat}</NoticeBox>
          </div>
        )}

        {rep && !blocked && (view === "single" || view === "invoice") && (
          <Suspense fallback={<p className="text-xs text-muted-foreground" role="status">Loading working and export...</p>}>
            <CalcReport
              title="GST calculation" tool="gst-calculator" gate={gate} filename={view === "invoice" ? "gst-invoice.csv" : "gst-calculation.csv"}
              steps={gstInvoiceSteps(rep, inclusive, supply, inr)}
              summary={`${view === "invoice" ? `Invoice of ${usable.length} lines` : `${inr(rep.base)} at ${rate}%`}: GST ${inr(rep.gst)}, total ${inr(rep.total)} (${rep.words})`}
              notes={[`Rounding: CGST and SGST/UTGST are equal halves; GST is worked out once per rate. ${rep.words}. India GST rates in force since 22 September 2025. Not tax advice.`]}
              tables={[reportTable({ caption: "GST by rate", heading: "GST by rate", rows, columns: taxColumns, rowKey: (r) => r.label })]}
            />
          </Suspense>
        )}

        <p className="text-xs text-muted-foreground text-center">
          CGST and SGST (or UTGST) are each worked out at half the rate and rounded separately, so the two halves are always equal. In Remove GST mode, GST is worked out as the total minus the base, so base plus GST always equals the amount you entered; when that GST would be an odd number of paise (so it could not split into two equal halves), the base is moved by 1 paisa to the nearer even figure. India GST rates in force since 22 September 2025. For businesses outside India see the VAT Calculator, which covers Canada, Australia, Singapore and many more countries. Not tax advice.
        </p>
      </div>
      <RatesNote id="gst" />
      <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
    </ToolContainer>
  );
}
